import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PortalIcon from './PortalIcon';

const emptyProfile = {
  nombre: '',
  apellido: '',
  razonSocial: '',
  cuit: '',
  domicilio: '',
  codigoPostal: '',
  telefono: '',
  tiendaNubeId: null,
  solicitaColecta: false,
  valorColecta: 18500,
  colectaOrigen: '',
  colectaHorario: '',
  colectaNotas: '',
};

function normalizeProfile(value = {}) {
  const source = value || {};
  return {
    ...emptyProfile,
    nombre: source.nombre || '',
    apellido: source.apellido || '',
    razonSocial: source.razonSocial || '',
    cuit: source.cuit || '',
    domicilio: source.domicilio || '',
    codigoPostal: source.codigoPostal || '',
    telefono: source.telefono || '',
    tiendaNubeId: source.tiendaNubeId ?? null,
    solicitaColecta: !!source.solicitaColecta,
    valorColecta: Number(source.valorColecta ?? 18500),
    colectaOrigen: source.colectaOrigen || '',
    colectaHorario: source.colectaHorario || '',
    colectaNotas: source.colectaNotas || '',
  };
}

function formatLocationAddress(location) {
  const address = location?.address;
  if (!address) return 'Dirección no informada';

  const line = [address.street, address.number, address.floor ? `Piso ${address.floor}` : null]
    .filter(Boolean)
    .join(' ');
  const place = [address.locality || address.city, address.province, address.postalCode]
    .filter(Boolean)
    .join(' · ');

  return [line, place].filter(Boolean).join(' — ') || 'Dirección no informada';
}

export default function PortalPerfil() {
  const { authRequestJson, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('personal');
  const [profile, setProfile] = useState(emptyProfile);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsReload, setLocationsReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState({ currentPassword: '', newPassword: '' });
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    authRequestJson('/portal/me/profile')
      .then((response) => {
        if (active) setProfile(normalizeProfile(response?.data));
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'No se pudo cargar el perfil.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [authRequestJson]);

  useEffect(() => {
    if (tab !== 'locations') return undefined;

    if (!profile.tiendaNubeId) {
      setLocations([]);
      setLocationsLoading(false);
      return undefined;
    }

    let active = true;
    setLocationsLoading(true);
    setError('');
    authRequestJson('/portal/me/tiendanube/locations')
      .then((response) => {
        if (active) setLocations(response?.data || []);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'No se pudieron cargar las locations de Tiendanube.');
      })
      .finally(() => {
        if (active) setLocationsLoading(false);
      });

    return () => { active = false; };
  }, [authRequestJson, locationsReload, profile.tiendaNubeId, tab]);

  const update = (field, value) => setProfile((previous) => ({ ...previous, [field]: value }));

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        nombre: profile.nombre,
        apellido: profile.apellido,
        razonSocial: profile.razonSocial,
        domicilio: profile.domicilio,
        codigoPostal: profile.codigoPostal,
        telefono: profile.telefono,
        solicitaColecta: profile.solicitaColecta,
        valorColecta: profile.valorColecta,
        colectaOrigen: profile.colectaOrigen,
        colectaHorario: profile.colectaHorario,
        colectaNotas: profile.colectaNotas,
      };
      const response = await authRequestJson('/portal/me', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (response?.data) setProfile(normalizeProfile(response.data));
      setMessage('Cambios guardados correctamente.');
    } catch (requestError) {
      setError(requestError.message || 'No se pudieron guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (password.newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setSavingPassword(true);
    setError('');
    setMessage('');
    try {
      await authRequestJson('/portal/me/password', {
        method: 'PUT',
        body: JSON.stringify(password),
      });
      setPassword({ currentPassword: '', newPassword: '' });
      setMessage('Contraseña actualizada.');
    } catch (requestError) {
      setError(requestError.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setSavingPassword(false);
    }
  };

  const deleteAccount = async () => {
    setError('');
    setMessage('');
    if (!deletePassword) {
      setError('Ingresá tu contraseña actual para eliminar la cuenta.');
      return;
    }
    if (deleteConfirmation.trim().toUpperCase() !== 'ELIMINAR') {
      setError('Escribí ELIMINAR para confirmar la baja de la cuenta.');
      return;
    }
    if (!window.confirm('La cuenta se desactivará y se desvinculará de Tiendanube. ¿Querés continuar?')) return;

    setDeletingAccount(true);
    try {
      await authRequestJson('/portal/me', {
        method: 'DELETE',
        body: JSON.stringify({ password: deletePassword, confirmation: deleteConfirmation }),
      });
      logout();
      navigate('/login?accountDeleted=1', { replace: true });
    } catch (requestError) {
      setError(requestError.message || 'No se pudo eliminar la cuenta.');
    } finally {
      setDeletingAccount(false);
    }
  };

  if (loading) return <div className="portal-app"><div className="portal-wrap portal-loading">Cargando tu perfil…</div></div>;

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        <header className="portal-page-header--split">
          <div>
            <p className="portal-kicker">Cuenta / Configuración</p>
            <h1>Tu cuenta</h1>
            <p>Administrá tus datos de contacto y la seguridad de tu acceso.</p>
          </div>
          <Link className="portal-btn portal-btn--outline" to="/portal"><PortalIcon name="back" size={16} /> Volver al portal</Link>
        </header>

        {message ? <div className="portal-alert portal-alert--success">{message}</div> : null}
        {error ? <div className="portal-alert portal-alert--error">{error}</div> : null}

        <div className="portal-profile-layout">
          <aside className="portal-card portal-profile-sidebar">
            <h3>Configuración</h3>
            <nav className="portal-profile-nav">
              <button type="button" className={tab === 'personal' ? 'is-active' : ''} onClick={() => { setTab('personal'); setError(''); }}><PortalIcon name="user" size={17} /> Datos personales</button>
              <button type="button" className={tab === 'locations' ? 'is-active' : ''} onClick={() => { setTab('locations'); setError(''); }}><PortalIcon name="box" size={17} /> Locations de Tiendanube</button>
              <button type="button" className={tab === 'seguridad' ? 'is-active' : ''} onClick={() => { setTab('seguridad'); setError(''); }}><PortalIcon name="settings" size={17} /> Seguridad</button>
            </nav>
          </aside>

          <section className="portal-card portal-profile-panel">
            {tab === 'personal' ? (
              <form className="portal-form" onSubmit={saveProfile}>
                <h2>Datos personales</h2>
                <p>Esta información identifica tu cuenta y sirve para preparar tus retiros.</p>
                <div className="portal-profile-card">
                  <h3>Identidad</h3>
                  <div className="portal-form-grid">
                    <div className="portal-field"><label className="portal-field-label">Nombre</label><input value={profile.nombre} onChange={(event) => update('nombre', event.target.value)} /></div>
                    <div className="portal-field"><label className="portal-field-label">Apellido</label><input value={profile.apellido} onChange={(event) => update('apellido', event.target.value)} /></div>
                    <div className="portal-field"><label className="portal-field-label">Razón social</label><input value={profile.razonSocial} onChange={(event) => update('razonSocial', event.target.value)} /></div>
                    <div className="portal-field"><label className="portal-field-label">CUIT</label><input value={profile.cuit} readOnly disabled /></div>
                  </div>
                </div>
                <div className="portal-profile-card">
                  <h3>Contacto y domicilio</h3>
                  <div className="portal-form-grid">
                    <div className="portal-field portal-field--wide"><label className="portal-field-label">Domicilio</label><input value={profile.domicilio} onChange={(event) => update('domicilio', event.target.value)} /></div>
                    <div className="portal-field"><label className="portal-field-label">Código postal</label><input value={profile.codigoPostal} onChange={(event) => update('codigoPostal', event.target.value)} /></div>
                    <div className="portal-field"><label className="portal-field-label">Teléfono</label><input value={profile.telefono} onChange={(event) => update('telefono', event.target.value)} /></div>
                  </div>
                </div>
                <div className="portal-profile-card">
                  <h3>Preferencias de retiro</h3>
                  <p className="portal-muted">Estos datos ayudan a coordinar una colecta cuando la solicites.</p>
                  <div className="portal-form-grid">
                    <label className="portal-check-label"><input type="checkbox" checked={profile.solicitaColecta} onChange={(event) => update('solicitaColecta', event.target.checked)} /> Solicitar colecta habitualmente</label>
                    <div className="portal-field"><label className="portal-field-label">Origen habitual</label><input value={profile.colectaOrigen} onChange={(event) => update('colectaOrigen', event.target.value)} /></div>
                    <div className="portal-field"><label className="portal-field-label">Horario</label><input value={profile.colectaHorario} onChange={(event) => update('colectaHorario', event.target.value)} /></div>
                    <div className="portal-field portal-field--wide"><label className="portal-field-label">Notas</label><textarea value={profile.colectaNotas} onChange={(event) => update('colectaNotas', event.target.value)} /></div>
                  </div>
                </div>
                <footer className="portal-form-footer"><button type="submit" className="portal-btn" disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button></footer>
              </form>
            ) : null}

            {tab === 'locations' ? (
              <div className="portal-form">
                <div className="portal-section-title">
                  <div>
                    <h2>Locations de Tiendanube</h2>
                    <p>Consultá los centros de distribución que Tiendanube envía al checkout y el orden que tiene configurado el merchant.</p>
                  </div>
                  <button type="button" className="portal-btn portal-btn--outline portal-btn--small" onClick={() => setLocationsReload((value) => value + 1)} disabled={locationsLoading || !profile.tiendaNubeId}>
                    {locationsLoading ? 'Actualizando…' : 'Actualizar'}
                  </button>
                </div>

                <div className="portal-alert portal-alert--success">
                  Las locations se administran en Tiendanube. ESA no crea ni edita centros desde este portal: recibe el <strong>origin</strong>, respeta la prioridad y solo prioriza locations con una ruta tarifaria válida para tu cliente y destino.
                </div>

                {!profile.tiendaNubeId ? (
                  <div className="portal-empty portal-empty--compact">
                    <strong>No hay una tienda Tiendanube vinculada.</strong>
                    <p>Vinculá tu tienda para consultar sus locations.</p>
                  </div>
                ) : locationsLoading ? (
                  <div className="portal-empty portal-empty--compact">Cargando locations…</div>
                ) : locations.length === 0 ? (
                  <div className="portal-empty portal-empty--compact">
                    <strong>Tiendanube no devolvió locations.</strong>
                    <p>Verificá la configuración de locations de la tienda y actualizá nuevamente.</p>
                  </div>
                ) : (
                  <div className="portal-table-wrap">
                    <table className="portal-table">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Dirección</th>
                          <th>Prioridad</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locations.map((location) => (
                          <tr key={location.id}>
                            <td>
                              <strong>{location.name || 'Sin nombre'}</strong>
                              <span className="portal-muted">ID {location.id}</span>
                            </td>
                            <td>{formatLocationAddress(location)}</td>
                            <td><strong>{location.priority ?? '—'}</strong><span className="portal-muted">Menor número = mayor prioridad</span></td>
                            <td>{location.isDefault ? <span className="portal-badge portal-badge--info">Predeterminada</span> : <span className="portal-muted">Disponible</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}

            {tab === 'seguridad' ? (
              <form className="portal-form" onSubmit={savePassword}>
                <h2>Seguridad</h2>
                <p>Actualizá tu contraseña periódicamente para mantener el acceso protegido.</p>
                <div className="portal-profile-card">
                  <h3>Cambiar contraseña</h3>
                  <div className="portal-form-grid">
                    <div className="portal-field"><label className="portal-field-label">Contraseña actual</label><input type="password" value={password.currentPassword} onChange={(event) => setPassword((previous) => ({ ...previous, currentPassword: event.target.value }))} autoComplete="current-password" required /></div>
                    <div className="portal-field"><label className="portal-field-label">Nueva contraseña</label><input type="password" value={password.newPassword} onChange={(event) => setPassword((previous) => ({ ...previous, newPassword: event.target.value }))} minLength="8" autoComplete="new-password" required /><span className="portal-field-hint">Mínimo 8 caracteres.</span></div>
                  </div>
                </div>
                <footer className="portal-form-footer"><button type="submit" className="portal-btn" disabled={savingPassword}>{savingPassword ? 'Actualizando…' : 'Actualizar contraseña'}</button></footer>
                <div className="portal-profile-card portal-profile-card--danger">
                  <h3>Eliminar cuenta</h3>
                  <p>La cuenta se desactiva, se revoca la vinculación con Tiendanube y se conserva el historial operativo para trazabilidad.</p>
                  <div className="portal-form-grid">
                    <div className="portal-field"><label className="portal-field-label">Contraseña actual</label><input type="password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} autoComplete="current-password" /></div>
                    <div className="portal-field"><label className="portal-field-label">Escribí ELIMINAR</label><input value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} autoComplete="off" /></div>
                  </div>
                  <button type="button" className="portal-btn portal-btn--danger" onClick={deleteAccount} disabled={deletingAccount}>{deletingAccount ? 'Eliminando cuenta…' : 'Eliminar mi cuenta'}</button>
                </div>
              </form>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
